/**
 * Contract for POST /api/analytics/session-score.
 *
 * The browser sends one reCAPTCHA v3 token per session; the server scores it
 * and writes one `bot_score` row to `events`. Identity comes from the
 * `sw_session_id` / `sw_anonymous_id` cookies, not the body, so a caller cannot
 * pin a score on somebody else's session by naming it.
 */

import { z } from 'zod';

/** The action the client mints its token under; Google echoes it back. */
export const SESSION_SCORE_ACTION = 'session_start';

/**
 * What a partner code may look like. The codes in `partners.txt` are short
 * lowercase words -- `dfm`, `latimes`, `siteimpact` -- so this is generous
 * against the real set and still narrow enough to keep junk off the dashboard.
 */
export const PARTNER_CODE_MAX = 64;
export const PARTNER_CODE_PATTERN = /^[A-Za-z0-9_.-]+$/;

/** reCAPTCHA tokens run to ~2 KB; anything far past that is not one. */
export const SessionScoreRequestSchema = z.object({
  token: z.string({ error: 'token is required' }).min(1, 'token is required').max(4096),
  ranking_variant: z.string().max(64).optional(),
  /**
   * The publisher that sent this visit, from `?p=` or the partner cookie.
   *
   * Attacker-controlled, like any query param, so it is bounded here rather
   * than trusted: it becomes a GROUP BY key on the dashboard, and an unbounded
   * one would let a single visit invent a thousand partners to scroll through.
   * A code that does not match is dropped, not rejected -- the score is worth
   * more than the attribution.
   */
  partner: z.string().max(PARTNER_CODE_MAX).regex(PARTNER_CODE_PATTERN).optional().catch(undefined),
});

export type SessionScoreRequest = z.infer<typeof SessionScoreRequestSchema>;

export const SessionScoreResponseSchema = z.object({
  recorded: z.literal(true),
  /** Null when Google rejected the token outright (expired, reused, forged). */
  score: z.number().nullable(),
  /**
   * True when this browser or account already had a real score and it was
   * reused, without asking Google. Optional so a tab running an older bundle
   * still parses the reply.
   */
  reused: z.boolean().optional(),
});

export type SessionScoreResponse = z.infer<typeof SessionScoreResponseSchema>;

export const SessionScoreErrorResponseSchema = z.object({
  error: z.string(),
});

export type SessionScoreErrorResponse = z.infer<typeof SessionScoreErrorResponseSchema>;

/**
 * The visitor filter the funnel dashboard and its drill-downs take. Lives here,
 * not beside the SQL in utils/clickhouse/bot-score.ts, so the page can import it
 * without pulling the ClickHouse client into the browser bundle.
 */
export const BOT_CLASS_FILTERS = [
  'all', 'human', 'likely_bot', 'rejected', 'unscored', 'declared_bot',
] as const;
export const BotClassFilterSchema = z.enum(BOT_CLASS_FILTERS);
export type BotClassFilter = z.infer<typeof BotClassFilterSchema>;
